/* eslint-disable react/no-danger -- when updating this component, consider replacing dangerous jsx properties */
import type { FC, ReactNode } from 'react';
import React, { Fragment } from 'react';

import { superbatchCSSRecorder, superbatchJSRecorder } from '@confluence/ssr-utilities';
import { deferScript, removeJquery, addSSRInlineStyleAttr } from '@confluence/dom-helpers';
import { PageSegmentLoadEnd } from '@confluence/browser-metrics';
import { CONTENT_RENDERER_METRIC } from '@confluence/view-content-perf-metrics';

export type tinySSRContentVariables = {
	body: string;
	superbatchCss: string;
	superbatchJs: string;
	css: string;
	js: string;
	/**
	 * Component to render after content is ready.
	 *
	 * Used for rendering performance metrics.
	 */
	renderWhenReady?: () => ReactNode;
	contentId: string;
};

/**
 * This component is only used for SSR
 */
export const TinyMCEServerFullRenderer: FC<tinySSRContentVariables> = ({
	body,
	superbatchCss,
	superbatchJs,
	css,
	js,
	contentId,
}) => {
	if (!superbatchCSSRecorder.get()) {
		superbatchCSSRecorder.add(superbatchCss);
	}

	if (js && !superbatchJSRecorder.get()) {
		// We don't need superbatch js if there isn't any content specific script.
		superbatchJSRecorder.addDeferTags(removeJquery(superbatchJs));
	}

	if (typeof document !== 'undefined') {
		// Remove connect inline scripts because we were not loading connect supporting files.
		// Creating an iframe in SSR result then destroy and re-create it in SPA creates a race condition.
		// In some cases the connect script would send event to the previous iframe after it has been destroyed.
		const wrapper = document.createElement('div');
		wrapper.innerHTML = body;
		wrapper.querySelectorAll('.ap-iframe-body-script').forEach((el) => el.remove());
		wrapper.querySelectorAll('.cc-macro-body-script').forEach((el) => el.remove());

		// Remove page summary report marker during ssr.same is done in Fabric EXTENSION_KEY.DETAILS_SUMMARY
		wrapper.querySelectorAll('.metadata-summary-macro').forEach((el) => {
			el.classList.remove('metadata-summary-macro');
		});

		body = wrapper.innerHTML;
	}

	return (
		<Fragment>
			{/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766 */}
			<div id="content" data-testid="TinyMCERendererTestId" className="page view legacy-renderer">
				{/* css tags should be placed inside content div so they can be cleaned up from page to page */}
				<div
					data-testid="css"
					key="css-container"
					dangerouslySetInnerHTML={{
						__html: addSSRInlineStyleAttr(css),
					}}
				/>
				<div
					dangerouslySetInnerHTML={{
						__html: body,
					}}
					id="main-content"
					data-inline-comments-target="true"
					// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
					className="wiki-content"
				/>
				{/* scriptContainer in next/packages/content-renderer/src/TinyMCEClientRenderer.tsx is in the content div */}
				<div
					key="js-container"
					data-testid="js"
					dangerouslySetInnerHTML={{
						__html: `${deferScript(js)}`,
					}}
				/>
			</div>
			<PageSegmentLoadEnd key={contentId} metric={CONTENT_RENDERER_METRIC} />
		</Fragment>
	);
};
